/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the errors, and displays a fallback UI.
 */

import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { Component } from "react";
import { isRouteErrorResponse, useRouteError, Link } from "react-router-dom";

interface ErrorBoundaryClassState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryClassProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Class-based error boundary for catching render errors.
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorBoundaryClassState> {
  constructor(props: ErrorBoundaryClassProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryClassState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console in development
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <ErrorDisplay
          title="Something went wrong"
          message={this.state.error?.message ?? "An unexpected error occurred"}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorDisplayProps {
  readonly title: string;
  readonly message: string;
  readonly status?: number;
  readonly onRetry?: () => void;
}

/**
 * Error display component for showing error information.
 */
function ErrorDisplay({ title, message, status, onRetry }: ErrorDisplayProps): ReactElement {
  return (
    <div className="flex min-h-[50vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
          <AlertTriangle className="h-8 w-8 text-error" aria-hidden="true" />
        </div>

        {status && <p className="mb-2 text-6xl font-bold text-text-secondary">{status}</p>}

        <h1 className="mb-4 text-2xl font-bold text-text-primary">{title}</h1>

        <p className="mb-8 text-text-secondary">{message}</p>

        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Link to="/" className="btn btn-primary">
            <Home className="mr-2 h-4 w-4" aria-hidden="true" />
            Go Home
          </Link>

          {onRetry && (
            <button onClick={onRetry} className="btn btn-secondary">
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Route error boundary for React Router errors.
 * Uses useRouteError hook to get error information.
 */
export function ErrorBoundary(): ReactElement {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    // Handle known route errors (404, 403, etc.)
    return (
      <ErrorDisplay
        title={error.status === 404 ? "Page Not Found" : "Error"}
        message={
          error.status === 404 ? "The page you are looking for does not exist." : error.statusText
        }
        status={error.status}
      />
    );
  }

  // Handle unknown errors
  const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";

  return (
    <ErrorDisplay
      title="Something went wrong"
      message={errorMessage}
      onRetry={() => window.location.reload()}
    />
  );
}

export { ErrorBoundaryClass };
